import { createContext, Dispatch, FunctionComponent, ReactNode, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { planoAPI, useGetDivisionsQuery, useGetMagasinsQuery, useGetSectionsQuery, useGetTypeProduitWebQuery, useLazyGetPlanoStatsQuery, } from "../../redux/features/cache";
import { Division } from "../../models/Division";
import { Section } from "../../models/Section";
import { Magasin } from "../../models/Magasin";
import { getNiveauFromIndex, getNiveauIndex, PlanoStatsItem, PlanoStatsMode, PlanoStatsNiveau, TypeProduitWeb } from "../../redux/features/types";
import { SortDescriptor } from '@progress/kendo-data-query';
import { FilterAccordionItem } from "../../components/FilterAccordion/FilterAccordion";
import { useAppDispatch } from "../../redux/store";

export enum ViewMode {
    PRODUCT = "product",
    STORE = "store",
}

export enum AccordionType {
    MAGASIN = "magasin",
    DIVISION = "division",
    SECTION = "section",
    PRODUIT = "produit",
    TYPE_PRODUIT_WEB = "type_produit_web",
}

export type ExpandedAccordionProductView = AccordionType;

export interface ProduitItem {
    txt: string;
}

export interface StatistiquesDataState {
    niveau: PlanoStatsNiveau;
    mode: PlanoStatsMode;
    ens: boolean;
    selectedMagasins: Magasin[];
    selectedDivisions: Division[];
    selectedSections: Section[];
    selectedTypeProduitWeb: TypeProduitWeb[];
    selectedProducts: ProduitItem[];

    magasinAccordionItems: FilterAccordionItem<Magasin>[];
    divisionAccordionItems: FilterAccordionItem<Division>[];
    sectionAccordionItems: FilterAccordionItem<Section>[];
    typeProduitWebAccordionItems: FilterAccordionItem<TypeProduitWeb>[];
    produitAccordionItems: FilterAccordionItem<ProduitItem>[];

    magasins: Magasin[];
    isLoadingMagasins: boolean;
    isFetchingMagasins: boolean;

    divisions: Division[];
    isLoadingDivisions: boolean;
    isFetchingDivisions: boolean;

    sections: Section[];
    isLoadingSections: boolean;
    isFetchingSections: boolean;

    typeProduitWeb: TypeProduitWeb[];
    isLoadingTypeProduitWeb: boolean;
    isFetchingTypeProduitWeb: boolean;

    products: ProduitItem[];
    isLoadingProducts: boolean;
    isFetchingProducts: boolean;

    planoStats: PlanoStatsItem[];
    isLoadingPlanoStats: boolean;
    isFetchingPlanoStats: boolean;

    magQueryParam: string;
    divQueryParam: string;
    sectionQueryParam: string;
    typeProduitWebQueryParam: string;
    prodQueryParam: string;
}

export interface StatistiquesDataActions {
    setMode: Dispatch<SetStateAction<StatistiquesDataState["mode"]>>;
    setEns: Dispatch<SetStateAction<StatistiquesDataState["ens"]>>;

    setMagasinAccordionItems: Dispatch<SetStateAction<StatistiquesDataState["magasinAccordionItems"]>>;
    setDivisionAccordionItems: Dispatch<SetStateAction<StatistiquesDataState["divisionAccordionItems"]>>;
    setSectionAccordionItems: Dispatch<SetStateAction<StatistiquesDataState["sectionAccordionItems"]>>;
    setTypeProduitWebAccordionItems: Dispatch<SetStateAction<StatistiquesDataState["typeProduitWebAccordionItems"]>>;
    setProduitAccordionItems: Dispatch<SetStateAction<StatistiquesDataState["produitAccordionItems"]>>;

    setSelectedMagasins: (...magasinIds: string[]) => void;
    setSelectedDivisions: (...divisionIds: string[]) => void;
    setSelectedSections: (...sectionIds: string[]) => void;
    setSelectedTypeProduitWeb: (...typeProduitWebIds: string[]) => void;
    setSelectedProducts: (...productIds: string[]) => void;

    clearSelectedMagasins: () => void;
    clearSelectedDivisions: () => void;
    clearSelectedSections: () => void;
    clearSelectedTypeProduitWeb: () => void;
    clearSelectedProducts: () => void;

    setNiveau: Dispatch<SetStateAction<StatistiquesDataState["niveau"]>>;
    reset: () => void;
    invalidateQueries: () => void;
}

const statistiqueDataInitialState: StatistiquesDataState = {
    niveau: PlanoStatsNiveau.DIVISION,
    mode: PlanoStatsMode.MAG_PROD,
    ens: false,
    selectedMagasins: [],
    selectedDivisions: [],
    selectedSections: [],
    selectedTypeProduitWeb: [],
    selectedProducts: [],

    magasinAccordionItems: [],
    divisionAccordionItems: [],
    sectionAccordionItems: [],
    typeProduitWebAccordionItems: [],
    produitAccordionItems: [],

    magasins: [],
    isLoadingMagasins: false,
    isFetchingMagasins: false,

    divisions: [],
    isLoadingDivisions: false,
    isFetchingDivisions: false,

    sections: [],
    isLoadingSections: false,
    isFetchingSections: false,

    typeProduitWeb: [],
    isLoadingTypeProduitWeb: false,
    isFetchingTypeProduitWeb: false,

    products: [],
    isLoadingProducts: false,
    isFetchingProducts: false,

    planoStats: [],
    isLoadingPlanoStats: false,
    isFetchingPlanoStats: false,

    magQueryParam: "",
    divQueryParam: "",
    sectionQueryParam: "",
    typeProduitWebQueryParam: "",
    prodQueryParam: "",
};

const emptyMagasins: Magasin[] = [];
const emptyDivisions: Division[] = [];
const emptySections: Section[] = [];
const emptyTypeProduitWeb: TypeProduitWeb[] = [];
const emptyProducts: ProduitItem[] = [];


export function useStatistiquesData(): StatistiquesDataState & StatistiquesDataActions {
    const dispatch = useAppDispatch();
    const [mode, setMode] = useState<StatistiquesDataState["mode"]>(statistiqueDataInitialState.mode);
    const [ens, setEns] = useState<StatistiquesDataState["ens"]>(statistiqueDataInitialState.ens);
    const [products, setProducts] = useState<StatistiquesDataState["products"]>(statistiqueDataInitialState.products);

    const [magasinAccordionItems, setMagasinAccordionItems_] = useState<StatistiquesDataState["magasinAccordionItems"]>(statistiqueDataInitialState.magasinAccordionItems);
    const [divisionAccordionItems, setDivisionAccordionItems_] = useState<StatistiquesDataState["divisionAccordionItems"]>(statistiqueDataInitialState.divisionAccordionItems);
    const [sectionAccordionItems, setSectionAccordionItems_] = useState<StatistiquesDataState["sectionAccordionItems"]>(statistiqueDataInitialState.sectionAccordionItems);
    const [typeProduitWebAccordionItems, setTypeProduitWebAccordionItems_] = useState<StatistiquesDataState["typeProduitWebAccordionItems"]>(statistiqueDataInitialState.typeProduitWebAccordionItems);
    const [produitAccordionItems, setProduitAccordionItems_] = useState<StatistiquesDataState["produitAccordionItems"]>(statistiqueDataInitialState.produitAccordionItems);

    const {
        data: magasins = emptyMagasins,
        isLoading: isLoadingMagasins,
        isFetching: isFetchingMagasins,
    } = useGetMagasinsQuery({}, {
        skip: false,
    });

    const selectedMagasins = useMemo(() => {
        const selectedMagItems = magasinAccordionItems.filter((item) => item.selected);
        if (magasins.length && selectedMagItems.length === magasins.length) {
            return magasins;
        }
        return selectedMagItems.length ? selectedMagItems.map((item) => item.data) : emptyMagasins;
    }, [magasins, magasinAccordionItems]);

    const magQueryParam = useMemo(() => {
        if (!selectedMagasins.length) {
            return "all";
        }

        const selected = selectedMagasins;
        const all = magasins;
        if (!selected.length) return "";
        if (all.length && selected.length === all.length) return "all";
        return selected.map((m) => m.mag_num).join(",");
    }, [selectedMagasins, magasins]);

    const {
        data: divisions = emptyDivisions,
        isLoading: isLoadingDivisions,
        isFetching: isFetchingDivisions,
    } = useGetDivisionsQuery({
        includeSections: false,
        treeView: false,
        plano: true,
        mag: magQueryParam,
        ens: ens,
    }, {
        skip: false, // (mode === PlanoStatsMode.MAG_PROD && !magQueryParam.length) || mode !== PlanoStatsMode.PROD,
    });

    const selectedDivisions = useMemo(() => {
        const selectedDivItems = divisionAccordionItems.filter((item) => item.selected);
        if (divisions.length && selectedDivItems.length === divisions.length) {
            return divisions;
        }
        return selectedDivItems.length ? selectedDivItems.map((item) => item.data) : emptyDivisions;
    }, [divisions, divisionAccordionItems]);

    const divQueryParam = useMemo(() => {
        const selected = selectedDivisions;
        const all = divisions;
        if (!selected.length) return "";
        if (all.length && selected.length === all.length) return "all";
        return selected.map((d) => d.div_num).join(",");
    }, [selectedDivisions, divisions]);

    const {
        data: sections = emptySections,
        isLoading: isLoadingSections,
        isFetching: isFetchingSections,
    } = useGetSectionsQuery({
        rech: "",
        simple: true,
        treeView: false,
        user: "",
        mag: magQueryParam,
        div: divQueryParam,
        ens: ens,
        plano: true,
    }, {
        skip: !divQueryParam.length,
    });

    const selectedSections = useMemo(() => {
        const selectedSectionItems = sectionAccordionItems.filter((item) => item.selected);
        if (sections.length && selectedSectionItems.length === sections.length) {
            return sections;
        }
        return selectedSectionItems.length ? selectedSectionItems.map((item) => item.data) : emptySections;
    }, [sections, sectionAccordionItems]);

    const sectionQueryParam = useMemo(() => {
        const selected = selectedSections;
        const all = sections;
        if (!selected.length) return "";
        if (all.length && selected.length === all.length) return "all";
        return selected.map((s) => s.plans_seq).join(",");
    }, [selectedSections, sections]);

    const {
        currentData: typeProduitWeb = emptyTypeProduitWeb,
        isLoading: isLoadingTypeProduitWeb,
        isFetching: isFetchingTypeProduitWeb,
    } = useGetTypeProduitWebQuery({
        section: sectionQueryParam,
        mag: magQueryParam,
        div: divQueryParam,
        plano: true,
        ens: ens,
    }, {
        skip: !sectionQueryParam.length || !divQueryParam.length,
    });

    const selectedTypeProduitWeb = useMemo(() => {
        const selectedTypeProduitWebItems = typeProduitWebAccordionItems.filter((item) => item.selected);
        if (typeProduitWeb.length && selectedTypeProduitWebItems.length === typeProduitWeb.length) {
            return typeProduitWeb;
        }
        return selectedTypeProduitWebItems.length ? selectedTypeProduitWebItems.map((item) => item.data) : [];
    }, [typeProduitWeb, typeProduitWebAccordionItems]);

    const typeProduitWebQueryParam = useMemo(() => {
        const selected = selectedTypeProduitWeb;
        const all = typeProduitWeb;
        if (!selected.length) return "";
        if (all.length && selected.length === all.length) return "all";
        return selected.map((t) => t.tpw_cd).join(",");
    }, [selectedTypeProduitWeb, typeProduitWeb]);

    const selectedProducts = useMemo(() => {
        const selectedProdItems = produitAccordionItems.filter((item) => item.selected);
        if (products.length && selectedProdItems.length === products.length) {
            return products;
        }
        return selectedProdItems.length ? selectedProdItems.map((item) => item.data) : emptyProducts;
    }, [products, produitAccordionItems]);

    const prodQueryParam = useMemo(() => {
        const selected = selectedProducts;
        const all = products;
        if (!selected.length) return "";
        if (all.length && selected.length === all.length) return "all";
        return selected.map((p) => p.txt.split(" ")[0]).join(",");
    }, [selectedProducts, products]);

    const [niveau, setNiveau] = useState<PlanoStatsNiveau>(statistiqueDataInitialState.niveau);
    const currentNiveau = useRef<PlanoStatsNiveau>(statistiqueDataInitialState.niveau);
    const lastFetchedParamsRef = useRef<{
        mag: string;
        div: string;
        section: string;
        tpw: string;
        produit: string;
        mode: PlanoStatsMode;
        niveau: PlanoStatsNiveau;
        ens: boolean;
    }>({
        mag: "",
        div: "",
        section: "",
        tpw: "",
        produit: "",
        mode: PlanoStatsMode.MAG_PROD,
        niveau: PlanoStatsNiveau.DIVISION,
        ens: false,
    });

    const [triggerLazyGetPlanoStats, resultLazyGetPlanoStats, lastPlanoStatsPromise] = useLazyGetPlanoStatsQuery();
    const {
        currentData: planoStats = [],
        isLoading: isLoadingPlanoStats,
        isFetching: isFetchingPlanoStats,
    } = resultLazyGetPlanoStats;

    const [triggerLazyGetPlanoStatsProducts, resultLazyGetPlanoStatsProducts] = useLazyGetPlanoStatsQuery();
    const {
        currentData: planoStatsProducts = [],
        isLoading: isLoadingProducts,
        isFetching: isFetchingProducts,
    } = resultLazyGetPlanoStatsProducts;

    useEffect(() => { currentNiveau.current = niveau; }, [niveau]);

    useEffect(() => {
        if (magasinAccordionItems.length) {
            const selectedMagItems = magasinAccordionItems.filter((item) => item.selected);
            if (!selectedMagItems.length) {
                clearSelectedDivisions();
                clearSelectedSections();
                clearSelectedTypeProduitWeb();
            }
        }
    }, [magasinAccordionItems]);

    useEffect(() => {
        if (divisionAccordionItems.length) {
            const selectedDivItems = divisionAccordionItems.filter((item) => item.selected);
            if (!selectedDivItems.length) {
                clearSelectedSections();
                clearSelectedTypeProduitWeb();
                clearSelectedProducts();
            }
        }
    }, [divisionAccordionItems]);

    useEffect(() => {
        if (sectionAccordionItems.length) {
            const selectedSectionItems = sectionAccordionItems.filter((item) => item.selected);
            if (!selectedSectionItems.length) {
                clearSelectedTypeProduitWeb();
                clearSelectedProducts();
            }
        }
    }, [sectionAccordionItems]);

    useEffect(() => {
        if (typeProduitWebAccordionItems.length) {
            const selectedTypeProduitWebItems = typeProduitWebAccordionItems.filter((item) => item.selected);
            if (!selectedTypeProduitWebItems.length) {
                clearSelectedProducts();
            }
        }
    }, [typeProduitWebAccordionItems]);

    useEffect(() => {
        const allSelected = magasins.length && magasinAccordionItems.length === magasins.length && magasinAccordionItems.every((item) => item.selected);
        const newMagItems = magasins.map((mag) => ({
            id: mag.mag_num,
            label: mag.mag_nom_aff,
            data: mag,
            selected: allSelected ? true : selectedMagasins.some((selected) => selected.mag_nom_aff === mag.mag_nom_aff),
        }));
        setMagasinAccordionItems_(newMagItems);

    }, [magasins]);

    useEffect(() => {
        const allSelected = divisionAccordionItems.length && divisionAccordionItems.every((item) => item.selected);
        const newDivItems = divisions.map((div) => ({
            id: div.div_num,
            label: div.div_nom_aff,
            data: div,
            selected: allSelected ? true : selectedDivisions.some((selected) => selected.div_nom_aff === div.div_nom_aff),
        }));
        setDivisionAccordionItems_(newDivItems);
    }, [divisions]);

    useEffect(() => {
        const allSelected = sectionAccordionItems.length && sectionAccordionItems.every((item) => item.selected);
        const newSectionItems = sections.map((section) => ({
            id: section.plans_nom,
            label: section.plans_nom,
            data: section,
            selected: allSelected ? true : selectedSections.some((selected) => selected.plans_nom === section.plans_nom),
        }));
        setSectionAccordionItems_(newSectionItems);
    }, [sections]);

    useEffect(() => {
        if (!typeProduitWeb.length) return;

        const allSelected = typeProduitWebAccordionItems.length && typeProduitWebAccordionItems.every((item) => item.selected);
        const newTypeProduitWebItems = typeProduitWeb.map((tpw) => ({
            id: tpw.tpw_cd,
            label: tpw.tpw_descr_aff,
            data: tpw,
            selected: allSelected ? true : selectedTypeProduitWeb.some((selected) => selected.tpw_cd === tpw.tpw_cd),
        }));
        setTypeProduitWebAccordionItems_(newTypeProduitWebItems);
    }, [typeProduitWeb]);

    useEffect(() => {
        const selectedProductIds = selectedProducts.map((p) => p.txt.split(" ")[0]);

        const allSelected = produitAccordionItems.length && produitAccordionItems.every((item) => item.selected);
        const newProduitItems = products.map((prod) => {
            const productId = prod.txt.split(" ")[0];
            return {
                id: prod.txt,
                label: prod.txt,
                data: prod,
                selected: allSelected ? true : selectedProductIds.some((id) => id === productId),
            }
        });
        setProduitAccordionItems_(newProduitItems);
    }, [products]);

    useEffect(() => {
        const niveauVal = currentNiveau.current;
        if (niveauVal === null && mode === PlanoStatsMode.MAG_PROD) {
            setNiveau(PlanoStatsNiveau.DIVISION);
        }

        if (niveauVal === PlanoStatsNiveau.DIVISION && selectedDivisions.length) {
            if (!selectedMagasins.length) {
                // Select all
                setSelectedMagasins(...magasins.map((m) => m.mag_nom_aff));
            }
            setNiveau(PlanoStatsNiveau.SECTION);
        } else if (niveauVal === PlanoStatsNiveau.SECTION && selectedTypeProduitWeb.length) {
            setNiveau(PlanoStatsNiveau.TYPE_PRODUIT_WEB);
        } else if (niveauVal === PlanoStatsNiveau.TYPE_PRODUIT_WEB && selectedTypeProduitWeb.length && mode === PlanoStatsMode.PROD) {
            setNiveau(PlanoStatsNiveau.PRODUIT);
        }
    }, [mode, selectedMagasins, selectedDivisions, selectedSections, selectedTypeProduitWeb, selectedProducts]);

    useEffect(() => {
        if ((mode === PlanoStatsMode.MAG_PROD && !selectedMagasins.length) || !selectedDivisions.length) {
            currentNiveau.current = PlanoStatsNiveau.DIVISION;
        } else if (!selectedSections.length) {
            currentNiveau.current = PlanoStatsNiveau.SECTION;
        } else if (!selectedTypeProduitWeb.length) {
            currentNiveau.current = PlanoStatsNiveau.TYPE_PRODUIT_WEB;
        } else if (!selectedProducts.length) {

        }
    }, [selectedMagasins, selectedDivisions, selectedSections, selectedTypeProduitWeb, selectedProducts]);

    useEffect(() => {
        if (!niveau) {
            return;
        }

        if (niveau === PlanoStatsNiveau.PRODUIT) {
            if (!selectedTypeProduitWeb.length || !selectedSections.length || !selectedDivisions.length) {
                return;
            }
        }
        const prevParams = lastFetchedParamsRef.current;
        const parentParamsChanged =
            prevParams.mag !== magQueryParam ||
            prevParams.div !== divQueryParam ||
            prevParams.section !== sectionQueryParam ||
            prevParams.tpw !== typeProduitWebQueryParam ||
            prevParams.mode !== mode ||
            prevParams.niveau !== niveau ||
            prevParams.ens !== ens;

        if (!parentParamsChanged && prevParams.produit === prodQueryParam) {
            return;
        }

        lastFetchedParamsRef.current = {
            mag: magQueryParam,
            div: divQueryParam,
            section: sectionQueryParam,
            tpw: typeProduitWebQueryParam,
            produit: prodQueryParam,
            mode: mode,
            niveau: niveau,
            ens: ens,
        };

        const currentNiveauIdx = getNiveauIndex(niveau);
        const mag = currentNiveauIdx >= getNiveauIndex(PlanoStatsNiveau.DIVISION) ? magQueryParam : "";
        const div = currentNiveauIdx >= getNiveauIndex(PlanoStatsNiveau.SECTION) ? divQueryParam : "";
        const section = currentNiveauIdx >= getNiveauIndex(PlanoStatsNiveau.TYPE_PRODUIT_WEB) ? sectionQueryParam : "";
        const tpw = currentNiveauIdx >= getNiveauIndex(PlanoStatsNiveau.PRODUIT) ? typeProduitWebQueryParam : "";

        if (mode === PlanoStatsMode.MAG_PROD && magQueryParam.length) {
            const result = triggerLazyGetPlanoStats({
                niveau: niveau,
                mag: mag,
                div: div,
                section: section,
                tpw: tpw,
                mode: mode,
                ens: ens,
            }, true);
            if (niveau === PlanoStatsNiveau.PRODUIT && parentParamsChanged) {
                result.unwrap().then((data) => {
                    const seen = new Set<string>();
                    const productItems = data
                        .filter((item) => {
                            const code = item.txt.split(" ")[0];
                            if (seen.has(code)) return false;
                            seen.add(code);
                            return true;
                        })
                        .map((item) => ({ txt: item.txt }));
                    setProducts(productItems);
                });
            }
        } else {
            if (mode === PlanoStatsMode.MAG_PROD && niveau === PlanoStatsNiveau.PRODUIT) {
                return;
            }

            triggerLazyGetPlanoStats({
                niveau: niveau,
                mag: "all",
                div: div,
                section: section,
                tpw: tpw,
                produit: prodQueryParam,
                mode: mode,
                ens: ens,
            }, true);

            if (mode === PlanoStatsMode.PROD && niveau === PlanoStatsNiveau.PRODUIT && selectedDivisions.length && selectedSections.length && selectedTypeProduitWeb.length) {
                if (parentParamsChanged) {
                    const result = triggerLazyGetPlanoStatsProducts({
                        niveau: PlanoStatsNiveau.PROD_ONLY,
                        mag: magQueryParam,
                        div: divQueryParam,
                        section: sectionQueryParam,
                        tpw: typeProduitWebQueryParam,
                        mode: PlanoStatsMode.MAG_PROD,
                        ens: ens,
                    }, true);
                    result.unwrap().then((data) => {
                        const seen = new Set<string>();
                        const productItems = data
                            .filter((item) => {
                                const code = item.txt.split(" ")[0];
                                if (seen.has(code)) return false;
                                seen.add(code);
                                return true;
                            })
                            .map((item) => ({ txt: item.txt }));
                        setProducts(productItems);
                    });
                }
            }
        }
    }, [mode, niveau, magQueryParam, divQueryParam, sectionQueryParam, typeProduitWebQueryParam, prodQueryParam, ens, triggerLazyGetPlanoStats]);

    useEffect(() => {
        let newNiveau = PlanoStatsNiveau.DIVISION;

        if (selectedDivisions.length) {
            newNiveau = PlanoStatsNiveau.SECTION;
        }
        if (selectedSections.length) {
            newNiveau = PlanoStatsNiveau.TYPE_PRODUIT_WEB;
        }
        if (selectedTypeProduitWeb.length) {
            newNiveau = PlanoStatsNiveau.PRODUIT;
        }

        setNiveau(newNiveau);
    }, [selectedMagasins, selectedDivisions, selectedSections, selectedTypeProduitWeb, selectedProducts]);

    const clearSelectedMagasins: StatistiquesDataActions["clearSelectedMagasins"] = useCallback(() => {
        const updatedMagItems = magasinAccordionItems.map((item) => ({ ...item, selected: false }));
        setMagasinAccordionItems_(updatedMagItems);
    }, [magasinAccordionItems]);

    const clearSelectedDivisions: StatistiquesDataActions["clearSelectedDivisions"] = useCallback(() => {
        const updatedDivItems = divisionAccordionItems.map((item) => ({ ...item, selected: false }));
        setDivisionAccordionItems_(updatedDivItems);
    }, [divisionAccordionItems]);

    const clearSelectedSections: StatistiquesDataActions["clearSelectedSections"] = useCallback(() => {
        const updatedSectionItems = sectionAccordionItems.map((item) => ({ ...item, selected: false }));
        setSectionAccordionItems_(updatedSectionItems);
    }, [sectionAccordionItems]);

    const clearSelectedTypeProduitWeb: StatistiquesDataActions["clearSelectedTypeProduitWeb"] = useCallback(() => {
        const updatedTypeProduitWebItems = typeProduitWebAccordionItems.map((item) => ({ ...item, selected: false }));
        setTypeProduitWebAccordionItems_(updatedTypeProduitWebItems);
    }, [typeProduitWebAccordionItems]);

    const clearSelectedProducts: StatistiquesDataActions["clearSelectedProducts"] = useCallback(() => {
        const updatedProduitItems = produitAccordionItems.map((item) => ({ ...item, selected: false }));
        setProduitAccordionItems_(updatedProduitItems);
    }, [produitAccordionItems]);

    const setSelectedMagasins: StatistiquesDataActions["setSelectedMagasins"] = useCallback((...magasinIds: string[]) => {
        let selectedCount = 0;
        const updatedMagItems = magasinAccordionItems.map((item) => {
            const isSelected = magasinIds.includes(item.data.mag_nom_aff);
            if (isSelected) selectedCount++;
            return {
                ...item,
                selected: isSelected,
            };
        });
        setMagasinAccordionItems(updatedMagItems);
    }, [magasinAccordionItems]);

    const setSelectedDivisions: StatistiquesDataActions["setSelectedDivisions"] = useCallback((...divisionIds: string[]) => {
        const updatedDivItems = divisionAccordionItems.map((item) => ({
            ...item,
            selected: divisionIds.includes(item.data.div_nom_aff),
        }));
        setDivisionAccordionItems_(updatedDivItems);
    }, [divisionAccordionItems]);

    const setSelectedSections: StatistiquesDataActions["setSelectedSections"] = useCallback((...sectionIds: string[]) => {
        const updatedSectionItems = sectionAccordionItems.map((item) => ({
            ...item,
            selected: sectionIds.includes(item.data.plans_nom),
        }));
        setSectionAccordionItems_(updatedSectionItems);
    }, [sectionAccordionItems]);

    const setSelectedTypeProduitWeb: StatistiquesDataActions["setSelectedTypeProduitWeb"] = useCallback((...typeProduitWebIds: string[]) => {
        const updatedTypeProduitWebItems = typeProduitWebAccordionItems.map((item) => ({
            ...item,
            selected: typeProduitWebIds.includes(item.data.tpw_cd),
        }));
        setTypeProduitWebAccordionItems_(updatedTypeProduitWebItems);
    }, [typeProduitWebAccordionItems]);

    const setSelectedProducts: StatistiquesDataActions["setSelectedProducts"] = useCallback((...productIds: string[]) => {
        const updatedProduitItems = produitAccordionItems.map((item) => ({
            ...item,
            selected: productIds.includes(item.data.txt),
        }));
        setProduitAccordionItems_(updatedProduitItems);
    }, [produitAccordionItems]);

    const setMagasinAccordionItems: StatistiquesDataActions["setMagasinAccordionItems"] = useCallback((items) => {
        setMagasinAccordionItems_(prev => {
            const newItems = typeof items === "function" ? items(prev) : items;
            const selectedCount = newItems.filter((item) => item.selected).length;
            if (selectedCount === 0) {
                setDivisionAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setSectionAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setTypeProduitWebAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setProduitAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
            }
            return newItems;
        });
    }, []);

    const setDivisionAccordionItems: StatistiquesDataActions["setDivisionAccordionItems"] = useCallback((items) => {
        setDivisionAccordionItems_(prev => {
            const newItems = typeof items === "function" ? items(prev) : items;
            const selectedCount = newItems.filter((item) => item.selected).length;
            if (selectedCount === 0) {
                setSectionAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setTypeProduitWebAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setProduitAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
            }
            return newItems;
        });
    }, []);

    const setSectionAccordionItems: StatistiquesDataActions["setSectionAccordionItems"] = useCallback((items) => {
        setSectionAccordionItems_(prev => {
            const newItems = typeof items === "function" ? items(prev) : items;
            const selectedCount = newItems.filter((item) => item.selected).length;
            if (selectedCount === 0) {
                setTypeProduitWebAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
                setProduitAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
            }
            return newItems;
        });
    }, []);

    const setTypeProduitWebAccordionItems: StatistiquesDataActions["setTypeProduitWebAccordionItems"] = useCallback((items) => {
        setTypeProduitWebAccordionItems_(prev => {
            const newItems = typeof items === "function" ? items(prev) : items;
            const selectedCount = newItems.filter((item) => item.selected).length;
            if (selectedCount === 0) {
                setProduitAccordionItems_(prev2 => prev2.map(i => ({ ...i, selected: false })));
            }
            return newItems;
        });
    }, []);

    const setProduitAccordionItems: StatistiquesDataActions["setProduitAccordionItems"] = useCallback((items) => {
        setProduitAccordionItems_(prev => {
            return typeof items === "function" ? items(prev) : items;
        });
    }, []);

    const invalidateQueries = useCallback(() => {
        console.log("Invalidating queries");
        dispatch(planoAPI.util.invalidateTags(["magasins", "divisions", "sections", "typeproduitweb", "planoStats"]));
    }, [dispatch]);

    const reset: StatistiquesDataActions["reset"] = useCallback(() => {
        clearSelectedMagasins();
        clearSelectedDivisions();
        clearSelectedSections();
        clearSelectedTypeProduitWeb();
        clearSelectedProducts();
        setMode(statistiqueDataInitialState.mode);
        setEns(statistiqueDataInitialState.ens);
        setNiveau(statistiqueDataInitialState.niveau);
        currentNiveau.current = statistiqueDataInitialState.niveau;
    }, [clearSelectedMagasins, clearSelectedDivisions, clearSelectedSections, clearSelectedTypeProduitWeb, clearSelectedProducts]);


    return useMemo(() => ({
        niveau,
        setNiveau,
        mode,
        ens,
        selectedMagasins,
        selectedDivisions,
        selectedSections,
        selectedTypeProduitWeb,
        selectedProducts,

        magasinAccordionItems,
        setMagasinAccordionItems,
        divisionAccordionItems,
        setDivisionAccordionItems,
        sectionAccordionItems,
        setSectionAccordionItems,
        typeProduitWebAccordionItems,
        setTypeProduitWebAccordionItems,
        produitAccordionItems,
        setProduitAccordionItems,

        magasins,
        isLoadingMagasins,
        isFetchingMagasins,
        divisions,
        isLoadingDivisions,
        isFetchingDivisions,
        sections,
        isLoadingSections,
        isFetchingSections,
        typeProduitWeb,
        isLoadingTypeProduitWeb,
        isFetchingTypeProduitWeb,
        products,
        isLoadingProducts,
        isFetchingProducts,
        planoStats,
        isLoadingPlanoStats,
        isFetchingPlanoStats,

        setMode,
        setEns,
        setSelectedMagasins,
        setSelectedDivisions,
        setSelectedSections,
        setSelectedProducts,
        setSelectedTypeProduitWeb,
        clearSelectedMagasins,
        clearSelectedDivisions,
        clearSelectedSections,
        clearSelectedProducts,
        clearSelectedTypeProduitWeb,
        reset,
        invalidateQueries,

        magQueryParam,
        divQueryParam,
        sectionQueryParam,
        typeProduitWebQueryParam,
        prodQueryParam,
    }), [
        niveau, setNiveau, mode, ens,
        selectedMagasins, selectedDivisions, selectedSections, selectedProducts,
        magasinAccordionItems, divisionAccordionItems, sectionAccordionItems, produitAccordionItems,
        magasins, isLoadingMagasins, isFetchingMagasins,
        divisions, isLoadingDivisions, isFetchingDivisions,
        sections, isLoadingSections, isFetchingSections,
        products, isLoadingProducts, isFetchingProducts,
        typeProduitWeb, isLoadingTypeProduitWeb, isFetchingTypeProduitWeb,
        planoStats, isLoadingPlanoStats, isFetchingPlanoStats,
        setMagasinAccordionItems, setDivisionAccordionItems, setSectionAccordionItems, setProduitAccordionItems,
        setMode, setEns, setSelectedMagasins, clearSelectedMagasins, clearSelectedDivisions, clearSelectedSections, clearSelectedProducts, reset, invalidateQueries,
        magQueryParam, divQueryParam, sectionQueryParam, typeProduitWebQueryParam, prodQueryParam,
        selectedTypeProduitWeb, setSelectedTypeProduitWeb, clearSelectedTypeProduitWeb,
    ]);
}

export interface StatistiquesState {
    currentView: ViewMode;
    gridSort: SortDescriptor[];
    productAccordionExpanded: ExpandedAccordionProductView | false;
}

export interface StatistiquesActions {
    setCurrentView: Dispatch<SetStateAction<StatistiquesState["currentView"]>>;
    setGridSort: Dispatch<SetStateAction<StatistiquesState["gridSort"]>>;
    setProductAccordionExpanded: Dispatch<SetStateAction<StatistiquesState["productAccordionExpanded"]>>;
}

export type StatistiquesHookType = StatistiquesState & StatistiquesActions;

const initialStatistiquesContextState: StatistiquesState = {
    currentView: ViewMode.PRODUCT,
    gridSort: [
        {
            field: "ventesJr",
            dir: "desc",
        }
    ],
    productAccordionExpanded: AccordionType.DIVISION,
};

export function useStatistiques_(): StatistiquesHookType {
    const [currentView, setCurrentView] = useState<StatistiquesState["currentView"]>(() => {
        const viewParam = new URLSearchParams(window.location.search).get("view");
        return viewParam === "store-stats" ? ViewMode.STORE : ViewMode.PRODUCT;
    });
    const [gridSort, setGridSort] = useState<StatistiquesState["gridSort"]>(initialStatistiquesContextState.gridSort);
    const [productAccordionExpanded, setProductAccordionExpanded] = useState<StatistiquesState["productAccordionExpanded"]>(initialStatistiquesContextState.productAccordionExpanded);

    return useMemo(() => ({
        currentView,
        gridSort,
        productAccordionExpanded,
        setCurrentView,
        setGridSort,
        setProductAccordionExpanded,
    }), [
        currentView, gridSort, productAccordionExpanded,
        setCurrentView, setGridSort, setProductAccordionExpanded,
    ]);
}

export type StatistiquesContextType = StatistiquesState & StatistiquesActions & StatistiquesDataState & StatistiquesDataActions;

export const statistiquesContext_ = createContext<StatistiquesContextType>({
    ...initialStatistiquesContextState,
    ...statistiqueDataInitialState,

    setCurrentView: () => { },
    setGridSort: () => { },
    setProductAccordionExpanded: () => { },
    setEns: () => { },
    setMode: () => { },
    setNiveau: () => { },

    setMagasinAccordionItems: () => { },
    setDivisionAccordionItems: () => { },
    setSectionAccordionItems: () => { },
    setProduitAccordionItems: () => { },
    setTypeProduitWebAccordionItems: () => { },

    setSelectedMagasins: (...magasinIds: string[]) => { },
    setSelectedDivisions: (...divisionIds: string[]) => { },
    setSelectedSections: (...sectionIds: string[]) => { },
    setSelectedTypeProduitWeb: (...typeProduitWebIds: string[]) => { },
    setSelectedProducts: (...productIds: string[]) => { },

    clearSelectedMagasins: () => { },
    clearSelectedDivisions: () => { },
    clearSelectedSections: () => { },
    clearSelectedTypeProduitWeb: () => { },
    clearSelectedProducts: () => { },

    reset: () => { },
    invalidateQueries: () => { },
});


function buildUrlFromFilters({
    view,
    mag,
    div,
    section,
    tpw,
    produit,
    ens,
}: {
    view: string;
    mag: string;
    div: string;
    section: string;
    tpw: string;
    produit: string;
    ens: boolean;
}): URL {
    const url = new URL(window.location.href);
    url.searchParams.set("view", view);
    if (mag) { url.searchParams.set("mag", mag); } else { url.searchParams.delete("mag"); }
    if (div) { url.searchParams.set("div", div); } else { url.searchParams.delete("div"); }
    if (section) { url.searchParams.set("section", section); } else { url.searchParams.delete("section"); }
    if (tpw) { url.searchParams.set("tpw", tpw); } else { url.searchParams.delete("tpw"); }
    if (produit) { url.searchParams.set("produit", produit); } else { url.searchParams.delete("produit"); }
    if (ens) {
        url.searchParams.set("ens", "1");
    } else {
        url.searchParams.delete("ens");
    }
    return url;
}

export const StatistiquesContextProvider: FunctionComponent<{ children: ReactNode }> = ({ children }) => {
    const statistiquesData = useStatistiquesData();
    const statistiques = useStatistiques_();

    type RestoreStep = "idle" | "mag" | "div" | "section" | "tpw" | "produit" | "done";
    const restoreStep = useRef<RestoreStep>("idle");
    const restoreTarget = useRef<{
        mag: string;
        div: string;
        section: string;
        tpw: string;
        produit: string;
    } | null>(null);
    const isRestoringFromUrl = useRef(false);
    const initialLoadDone = useRef(false);
    const [restoreKey, setRestoreKey] = useState(0);

    const startRestore = useCallback((params: URLSearchParams) => {
        const mag = params.get("mag") ?? "";
        const div = params.get("div") ?? "";
        const section = params.get("section") ?? "";
        const tpw = params.get("tpw") ?? "";
        const produit = params.get("produit") ?? "";

        if (!mag && !div && !section && !tpw && !produit) return;

        restoreTarget.current = { mag, div, section, tpw, produit };
        restoreStep.current = mag ? "mag" : div ? "div" : section ? "section" : tpw ? "tpw" : "produit";
        isRestoringFromUrl.current = true;
    }, []);

    useEffect(() => {
        if (initialLoadDone.current) return;
        initialLoadDone.current = true;

        const params = new URLSearchParams(window.location.search);
        statistiques.setCurrentView(params.get("view") === "store-stats" ? ViewMode.STORE : ViewMode.PRODUCT);
        statistiquesData.setEns(params.get("ens") === "1");
        startRestore(params);
    }, []);

    useEffect(() => {
        const target = restoreTarget.current;
        if (!target || restoreStep.current === "idle" || restoreStep.current === "done") return;
        const step = restoreStep.current;

        const prevLevelApplied = (paramVal: string, appliedIds: string[]): boolean => {
            if (!paramVal) return true;
            if (paramVal === "all") return appliedIds.length > 0;
            const targetIds = paramVal.split(",").sort().join(",");
            return appliedIds.slice().sort().join(",") === targetIds;
        };

        if (step === "mag") {
            if (!statistiquesData.magasins.length) return;
            if (target.mag !== "all" && !statistiquesData.magasins.some((m) => target.mag.split(",").includes(String(m.mag_num)))) return;

            statistiquesData.setMagasinAccordionItems((prev) =>
                prev.map((item) => ({ ...item, selected: target.mag === "all" || target.mag.split(",").includes(String(item.data.mag_num)) }))
            );
            restoreStep.current = target.div ? "div" : "done";
            if (restoreStep.current === "done") { restoreTarget.current = null; isRestoringFromUrl.current = false; }
        }
        else if (step === "div") {
            if (!prevLevelApplied(target.mag, statistiquesData.selectedMagasins.map((m) => String(m.mag_num)))) return;
            if (!statistiquesData.divisions.length) return;
            if (target.div !== "all" && !statistiquesData.divisions.some((d) => target.div.split(",").includes(String(d.div_num)))) return;

            statistiquesData.setDivisionAccordionItems((prev) =>
                prev.map((item) => ({ ...item, selected: target.div === "all" || target.div.split(",").includes(String(item.data.div_num)) }))
            );
            restoreStep.current = target.section ? "section" : "done";
            if (restoreStep.current === "done") { restoreTarget.current = null; isRestoringFromUrl.current = false; }
        }
        else if (step === "section") {
            if (!prevLevelApplied(target.div, statistiquesData.selectedDivisions.map((d) => String(d.div_num)))) return;
            if (!statistiquesData.sections.length) return;
            if (target.section !== "all" && !statistiquesData.sections.some((s) => target.section.split(",").includes(String(s.plans_seq)))) return;

            statistiquesData.setSectionAccordionItems((prev) =>
                prev.map((item) => ({ ...item, selected: target.section === "all" || target.section.split(",").includes(String(item.data.plans_seq)) }))
            );
            restoreStep.current = target.tpw ? "tpw" : "done";
            if (restoreStep.current === "done") { restoreTarget.current = null; isRestoringFromUrl.current = false; }
        }
        else if (step === "tpw") {
            if (!prevLevelApplied(target.section, statistiquesData.selectedSections.map((s) => String(s.plans_seq)))) return;
            if (!statistiquesData.typeProduitWeb.length) return;
            if (target.tpw !== "all" && !statistiquesData.typeProduitWeb.some((t) => target.tpw.split(",").includes(String(t.tpw_cd)))) return;

            statistiquesData.setTypeProduitWebAccordionItems((prev) =>
                prev.map((item) => ({ ...item, selected: target.tpw === "all" || target.tpw.split(",").includes(String(item.data.tpw_cd)) }))
            );
            restoreStep.current = target.produit ? "produit" : "done";
            if (restoreStep.current === "done") { restoreTarget.current = null; isRestoringFromUrl.current = false; }
        }
        else if (step === "produit") {
            console.log("Set produit url parameter");
            if (!prevLevelApplied(target.tpw, statistiquesData.selectedTypeProduitWeb.map((t) => String(t.tpw_cd)))) return;
            if (!statistiquesData.products.length) return;
            if (target.produit !== "all" && !statistiquesData.products.some((p) =>
                target.produit.split(",").some((cd) => p.txt.startsWith(cd + " ") || p.txt === cd)
            )) return;

            statistiquesData.setProduitAccordionItems((prev) =>
                prev.map((item) => ({
                    ...item,
                    selected: target.produit === "all" ||
                        target.produit.split(",").some((cd) => item.data.txt.startsWith(cd + " ") || item.data.txt === cd),
                }))
            );
            restoreStep.current = "done";
            restoreTarget.current = null;
            isRestoringFromUrl.current = false;
        }
    }, [
        restoreKey,
        statistiquesData.magasins,
        statistiquesData.divisions,
        statistiquesData.sections,
        statistiquesData.typeProduitWeb,
        statistiquesData.products,
        statistiquesData.selectedMagasins,
        statistiquesData.selectedDivisions,
        statistiquesData.selectedSections,
        statistiquesData.selectedTypeProduitWeb,
    ]);

    const isPushingUrl = useRef(false);
    const pushTimeoutRef = useRef<number | null>(null);

    useEffect(() => {
        if (isRestoringFromUrl.current) return;

        const viewStr = statistiques.currentView === ViewMode.STORE ? "store-stats" : "product-stats";

        const newUrl = buildUrlFromFilters({
            view: viewStr,
            mag: statistiquesData.magQueryParam,
            div: statistiquesData.divQueryParam,
            section: statistiquesData.sectionQueryParam,
            tpw: statistiquesData.typeProduitWebQueryParam,
            produit: statistiquesData.prodQueryParam,
            ens: statistiquesData.ens,
        });

        if (pushTimeoutRef.current !== null) {
            window.clearTimeout(pushTimeoutRef.current);
        }

        pushTimeoutRef.current = window.setTimeout(() => {
            const currentSearch = window.location.search;
            const newSearch = newUrl.search;

            if (currentSearch !== newSearch && !isPushingUrl.current && !isRestoringFromUrl.current) {
                isPushingUrl.current = true;
                window.history.pushState(
                    {
                        view: viewStr,
                        mag: statistiquesData.magQueryParam,
                        div: statistiquesData.divQueryParam,
                        section: statistiquesData.sectionQueryParam,
                        tpw: statistiquesData.typeProduitWebQueryParam,
                        produit: statistiquesData.prodQueryParam,
                        ens: statistiquesData.ens,
                    },
                    "",
                    newUrl.toString()
                );
                isPushingUrl.current = false;
            }
        }, 50);

        return () => {
            if (pushTimeoutRef.current !== null) {
                window.clearTimeout(pushTimeoutRef.current);
            }
        };
    }, [
        statistiques.currentView,
        statistiquesData.magQueryParam,
        statistiquesData.divQueryParam,
        statistiquesData.sectionQueryParam,
        statistiquesData.typeProduitWebQueryParam,
        statistiquesData.prodQueryParam,
        statistiquesData.magasins,
        statistiquesData.divisions,
        statistiquesData.sections,
        statistiquesData.typeProduitWeb,
        statistiquesData.products,
        statistiquesData.ens,
    ]);

    useEffect(() => {
        const popStateHandler = (_ev: PopStateEvent) => {
            const params = new URLSearchParams(window.location.search);

            statistiques.setCurrentView(params.get("view") === "store-stats" ? ViewMode.STORE : ViewMode.PRODUCT);
            statistiquesData.setEns(params.get("ens") === "1");

            const magParam = params.get("mag");
            if (magParam) {
                const magNums = magParam.split(",");
                statistiquesData.setMagasinAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: magParam === "all" || magNums.includes(String(item.data.mag_num)) }))
                );
            } else {
                statistiquesData.setMagasinAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: false }))
                );
            }

            const div = params.get("div") ?? "";
            const section = params.get("section") ?? "";
            const tpw = params.get("tpw") ?? "";
            const produit = params.get("produit") ?? "";
            const mag = magParam ?? "";

            if (!div) {
                statistiquesData.setDivisionAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: false }))
                );
            }
            if (!section) {
                statistiquesData.setSectionAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: false }))
                );
            }
            if (!tpw) {
                statistiquesData.setTypeProduitWebAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: false }))
                );
            }
            if (!produit) {
                console.log("set produit 1");
                statistiquesData.setProduitAccordionItems((prev) =>
                    prev.map((item) => ({ ...item, selected: false }))
                );
            }


            if (div || section || tpw || produit) {
                restoreTarget.current = { mag, div, section, tpw, produit };
                restoreStep.current = div ? "div" : section ? "section" : tpw ? "tpw" : "produit";
                isRestoringFromUrl.current = true;
                setRestoreKey((k) => k + 1);
            } else {
                restoreTarget.current = null;
                restoreStep.current = "idle";
                isRestoringFromUrl.current = false;
            }
        };

        window.addEventListener("popstate", popStateHandler);
        return () => window.removeEventListener("popstate", popStateHandler);
    }, [statistiques, statistiquesData, startRestore]);

    useEffect(() => {
        if (statistiques.currentView === ViewMode.PRODUCT) {
            statistiquesData.setMode(PlanoStatsMode.MAG_PROD);
        } else {
            statistiquesData.setMode(PlanoStatsMode.PROD);
        }
    }, [statistiques.currentView]);

    useEffect(() => {
        if (statistiques.currentView === ViewMode.PRODUCT && !statistiquesData.selectedMagasins.length) {
            statistiques.setProductAccordionExpanded(AccordionType.MAGASIN);
        } else if (!statistiquesData.selectedDivisions.length) {
            statistiques.setProductAccordionExpanded(AccordionType.DIVISION);
        } else if (!statistiquesData.selectedSections.length) {
            statistiques.setProductAccordionExpanded(AccordionType.SECTION);
        } else if (!statistiquesData.selectedTypeProduitWeb.length) {
            statistiques.setProductAccordionExpanded(AccordionType.TYPE_PRODUIT_WEB);
        } else if (statistiques.currentView === ViewMode.STORE && !statistiquesData.selectedProducts.length) {
            statistiques.setProductAccordionExpanded(AccordionType.PRODUIT);
        }
    }, [statistiques.currentView, statistiquesData.selectedMagasins.length, statistiquesData.selectedDivisions.length, statistiquesData.selectedSections.length, statistiquesData.selectedTypeProduitWeb.length, statistiquesData.selectedProducts.length]);

    const contextValue = useMemo(() => ({
        ...statistiquesData,
        ...statistiques,
    }), [statistiquesData, statistiques]);

    return (
        <statistiquesContext_.Provider value={contextValue}>
            {children}
        </statistiquesContext_.Provider>
    );
};

export function useStatistiquesContext(): StatistiquesContextType {
    const context = useContext(statistiquesContext_);
    if (!context) {
        throw new Error("useStatistiquesContext must be used within a StatistiquesContextProvider");
    }
    return context;
}
